// [PURPOSE]: Skill proficiency data format conversion utilities
// [WHY]: The wizard stores skill proficiencies as a display-name array
//        (['Athletics', 'Stealth']) while the context state / SkillsPanel uses
//        a camelCase keyed object with numeric levels ({athletics: 1, stealth: 1}).
//        Legacy character saves may also use boolean values ({athletics: true}).
//        These helpers convert between the formats in one place so all four
//        persistence paths (wizard, load, import, autosave) stay in sync.
// [INTEGRATION]: Consumed by App.jsx. Extracted from App.jsx during Session 2 so
//                the logic is unit-testable — the original in-line definitions
//                were never exercised by tests and the silent-proficiency bug
//                it was meant to fix (Athletics +3 vs +5) shipped undetected.

// [DATA]: Maps wizard display names to SkillsPanel camelCase keys.
//         All 18 D&D 5e skills covered (PHB p.174).
export const SKILL_NAME_TO_KEY = {
  'Acrobatics': 'acrobatics',
  'Animal Handling': 'animalHandling',
  'Arcana': 'arcana',
  'Athletics': 'athletics',
  'Deception': 'deception',
  'History': 'history',
  'Insight': 'insight',
  'Intimidation': 'intimidation',
  'Investigation': 'investigation',
  'Medicine': 'medicine',
  'Nature': 'nature',
  'Perception': 'perception',
  'Performance': 'performance',
  'Persuasion': 'persuasion',
  'Religion': 'religion',
  'Sleight of Hand': 'sleightOfHand',
  'Stealth': 'stealth',
  'Survival': 'survival',
};

// [PURPOSE]: Convert wizard's skill name array to SkillsPanel's numeric level object.
// [INPUT]: Array of display-name strings, e.g. ['Athletics', 'Stealth'].
// [OUTPUT]: { athletics: 1, stealth: 1 } — numeric 1 means proficient, 2 means expertise.
// [INTEGRATION]: Called by handleCharacterWizardComplete, character load, import.
export function convertSkillNamesToObject(skillNames) {
  const result = {};
  if (!Array.isArray(skillNames)) return result;
  skillNames.forEach(name => {
    const key = SKILL_NAME_TO_KEY[name];
    if (key) {
      result[key] = 1;
    }
  });
  return result;
}

// [PURPOSE]: Normalize legacy skill proficiency formats to numeric levels.
// [WHY]: Some saved characters store boolean values (true) instead of numeric (1).
//        This lets us accept both formats on load without data loss.
// [OUTPUT]: { athletics: 1, ... } — always numeric, zero/false/missing entries dropped.
export function normalizeSkillProficiencies(data) {
  if (!data || typeof data !== 'object') return {};
  const result = {};
  Object.entries(data).forEach(([key, value]) => {
    if (value === true) result[key] = 1;
    else if (typeof value === 'number' && value > 0) result[key] = value;
  });
  return result;
}

// [PURPOSE]: Format-tolerant "is this skill proficient?" check.
// [WHY]: 2026-07-12 table-playtest stall — LevelUpWizard's expertise step gated
//        picks on `playerStats.skills` (legacy boolean path nothing writes) instead
//        of the canonical numeric skillProficiencies map, so every skill read as
//        non-proficient and the wizard hard-blocked. Any consumer that needs a
//        proficiency answer goes through this one helper so both formats are
//        accepted in one place.
// [INPUT]: profs — numeric map ({athletics: 1|2}) OR legacy boolean map ({athletics: true}).
export function isSkillProficient(profs, skillKey) {
  if (!profs || !skillKey) return false;
  const value = profs[skillKey];
  return value === true || (typeof value === 'number' && value > 0);
}
